import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { DeleteOutlined } from "../../../../helpers/icons/antDesignIcons";
import "./CompanyEditCompanyPage.scss";
import {
  getCompanyDataByCompanyId,
  softDeleteCompanyByCompanyId,
  updateCompany,
} from "../../../../api/settingsCompanyApi";
import useTranslation from "../../../../hooks/useTranslation";
import UserContext from "../../../../context/UserContext"; // <-- Added for Super Admin check

const CompanyEditCompanyPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("settingsPage");
  const { user } = useContext(UserContext); // <-- Grab the user
  const [companyData, setCompanyData] = useState(null);
  const [errors, setErrors] = useState({});

  const [showDeleteCompanyModal, setShowDeleteCompanyModal] = useState(false);
  const handleDeleteCompanyModalClose = () => setShowDeleteCompanyModal(false);
  const handleDeleteCompanyModalShow = () => setShowDeleteCompanyModal(true);

  // Check if the user is a super admin
  const isUserSuperAdmin = user && (user.roles === "0" || user.roles === 0);

  useEffect(() => {
    if (!companyData) {
      // Fetch user info if not passed via state
      const fetchCompanyInfo = async () => {
        try {
          const response = await getCompanyDataByCompanyId(companyId);
          setCompanyData(response);
        } catch (error) {
          console.error("Error fetching company info:", error);
        }
      };
      fetchCompanyInfo();
    }
  }, [companyData, companyId]);

  if (!companyData) {
    return <div>Loading...</div>;
  }

  const handleInputChange = (event) => {
    const { name, type, checked, value } = event.target;
    setCompanyData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Delete company modal functionality
  const handleDeleteCompany = async (event, companyid) => {
    event.preventDefault();
    try {
      const response = await softDeleteCompanyByCompanyId(companyid);

      if (response.status === 200) {
        toast.success(t("delete_company_success"));
        navigate("/settings/company");
      } else {
        toast.error(t("delete_company_error"));
      }
    } catch (error) {
      toast.error(t("delete_company_error"));
    }
  };

  // Relaxed Validation (Only Company Name is required)
  const validate = () => {
    const errors = {};

    // REQUIRED FIELD
    if (!companyData.companyname || companyData.companyname.trim().length < 3) {
      errors.companyname = t("validation_error_company_name");
    }

    // OPTIONAL FIELDS (Only validate if they are not empty)
    if (companyData.companycity && companyData.companycity.trim() !== "") {
      if (companyData.companycity.trim().length < 3) {
        errors.companycity = t("validation_error_company_city");
      }
    }

    if (companyData.street && companyData.street.trim() !== "") {
      if (companyData.street.trim().length < 3) {
        errors.street = t("validation_error_street");
      }
    }

    if (companyData.housenr && companyData.housenr.trim() !== "") {
      if (companyData.housenr.trim().length > 20) {
        errors.housenr = t("validation_error_house_number");
      }
    }

    if (companyData.taxnumber && companyData.taxnumber.trim() !== "") {
      if (!/^\d{8}-\d{1}-\d{2}$/.test(companyData.taxnumber)) {
        errors.taxnumber = t("add_new_company_error_taxnumber");
      }
    }

    if (companyData.phonenumber && companyData.phonenumber.trim() !== "") {
      if (!/^\+?[0-9]{6,15}$/.test(companyData.phonenumber)) {
        errors.phonenumber = t("validation_error_phone_number");
      }
    }

    if (companyData.companyemail && companyData.companyemail.trim() !== "") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.companyemail)) {
        errors.companyemail = t("validation_error_email");
      }
    }

    if (companyData.website && companyData.website.trim() !== "") {
      if (!/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-]*)*$/.test(companyData.website)) {
        errors.website = t("validation_error_website");
      }
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!companyData) {
      toast.error("Company data is not loaded.");
      return;
    }

    const { companyid, ...updatedFields } = companyData;

    // Validate company data
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Object.values(validationErrors).forEach((error) => {
        toast.error(error);
      });
      return;
    }

    try {
      const response = await updateCompany(companyid, updatedFields);

      if (response.status === 200) {
        toast.success("Cégadatok sikeresen módosítva!");
        navigate("/settings/company");
      } else {
        toast.error("Hiba történt a cégadatok módosítása során.");
      }
    } catch (error) {
      toast.error("An error occurred while updating company data.");
      console.error("Error updating company data:", error);
    }
  };

  const handleBackButton = () => {
    navigate("/settings/company");
  };

  return (
    <div className="container company-edit-company-page__container navbar-margin">
      <div className="page-title-wrapper">
        <h1 className="page-title">{t("modify_company_data")}</h1>
        <div className="page-title-delete__wrapper">
          <p className="page-title-delete_p">{t("delete_company")}</p>
          <button
            className="page-title-delete__button"
            onClick={handleDeleteCompanyModalShow}
          >
            <DeleteOutlined />
          </button>
        </div>
      </div>
      <Modal
        show={showDeleteCompanyModal}
        onHide={handleDeleteCompanyModalClose}
        className="company-edit-company-page__delete-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("delete_company")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("delete_company_message")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleDeleteCompanyModalClose}
            className="btn btn-secondary"
          >
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={(event) =>
              handleDeleteCompany(event, companyData.companyid)
            }
            className="btn btn-danger"
          >
            {t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="company-edit-company-page__content">
        <Form
          onSubmit={handleSubmit}
          className="company-edit-company-page-form"
        >
          <Form.Group controlId="formCompanyName">
            <Form.Label>{t("company_name")}*</Form.Label>
            <Form.Control
              type="text"
              name="companyname"
              value={companyData.companyname || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.companyname}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyCity">
            <Form.Label>{t("company_city")}</Form.Label>
            <Form.Control
              type="text"
              name="companycity"
              value={companyData.companycity || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.companycity}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyStreet">
            <Form.Label>{t("company_street")}</Form.Label>
            <Form.Control
              type="text"
              name="street"
              value={companyData.street || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.street}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyHouseNumber">
            <Form.Label>{t("company_house_number")}</Form.Label>
            <Form.Control
              type="text"
              name="housenr"
              value={companyData.housenr || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.housenr}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyTaxNumber">
            <Form.Label>{t("company_tax_number")}</Form.Label>
            <Form.Control
              type="text"
              name="taxnumber"
              value={companyData.taxnumber || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.taxnumber}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyPhoneNumber">
            <Form.Label>{t("company_phone_number")}</Form.Label>
            <Form.Control
              type="text"
              name="phonenumber"
              value={companyData.phonenumber || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.phonenumber}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyEmail">
            <Form.Label>{t("email")}</Form.Label>
            <Form.Control
              type="email"
              name="companyemail"
              value={companyData.companyemail || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.companyemail}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyWebsite">
            <Form.Label>{t("website")}</Form.Label>
            <Form.Control
              type="text"
              name="website"
              value={companyData.website || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.website}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyLicences">
            <Form.Label>{t("licenses")}</Form.Label>
            <Form.Control
              type="text"
              name="licences"
              value={companyData.licences || ""}
              onChange={handleInputChange}
              isInvalid={!!errors.licences}
            />
          </Form.Group>
          <Form.Group controlId="formCompanyStatus">
            <Form.Label>{t("status")}*</Form.Label>
            <Form.Select
              name="status"
              value={companyData.status === true ? "true" : "false"}
              onChange={(e) =>
                setCompanyData({
                  ...companyData,
                  status: e.target.value === "true",
                })
              }
            >
              <option value="true">{t("active")}</option>
              <option value="false">{t("inactive")}</option>
            </Form.Select>
          </Form.Group>
          
          {/* RESTRICTED: Only Super Admins see this */}
          {isUserSuperAdmin && (
            <Form.Group controlId="formCompanyShrinkageControl">
              <Form.Label>{t("shrinkage_control") || "Extra gyakori riasztások"}</Form.Label>
              <Form.Control
                as="select"
                value={companyData.shrinkage_control === true ? "true" : "false"}
                onChange={(e) =>
                  setCompanyData({
                    ...companyData,
                    shrinkage_control: e.target.value === "true",
                  })
                }
              >
                <option value="true">{t("active") || "Aktív"}</option>
                <option value="false">{t("inactive") || "Inaktív"}</option>
              </Form.Control>
            </Form.Group>
          )}

          <div className="company-edit-company-page__button__wrapper mt-4">
            <Button
              type="submit"
              className="btn btn-save company-edit-company-page__save-button"
            >
              {t("save")}
            </Button>
            <Button
              className="btn btn-secondary company-edit-company-back-button"
              onClick={handleBackButton}
            >
              {t("cancel")}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CompanyEditCompanyPage;